import { PasswordResetError, apiClient } from '@/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import Alert from '@/components/Alert';
import AuthLayout from '@/components/AuthLayout';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import Translator, { useTranslation } from 'components/i18n/Translator';

const schema = z
  .object({
    password: z.string().min(8).max(128),
    confirmPassword: z.string()
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'mismatch'
  });

type FormValues = z.infer<typeof schema>;

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{20,}$/;

type TokenStatus =
  | 'checking'
  | 'valid'
  | 'invalid'
  | 'error'
  | 'rateLimited';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = searchParams.get('token') || '';
  const tokenShapeValid = TOKEN_PATTERN.test(token);

  const [pageError, setPageError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>(
    tokenShapeValid ? 'checking' : 'invalid'
  );
  const [checkRetryAfter, setCheckRetryAfter] = useState<number | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const invalidHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' }
  });

  const { ref: rhfPasswordRef, ...passwordRegister } = register('password');

  useEffect(() => {
    if (retryAfter === null) return;
    if (retryAfter <= 0) {
      setRetryAfter(null);
      return;
    }
    const id = setInterval(() => {
      setRetryAfter((prev) => (prev === null || prev <= 1 ? null : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [retryAfter]);

  useEffect(() => {
    if (checkRetryAfter === null) return;
    if (checkRetryAfter <= 0) {
      setCheckRetryAfter(null);
      return;
    }
    const id = setInterval(() => {
      setCheckRetryAfter((prev) =>
        prev === null || prev <= 1 ? null : prev - 1
      );
    }, 1000);
    return () => clearInterval(id);
  }, [checkRetryAfter]);

  useEffect(() => {
    if (!tokenShapeValid) return;
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const result = await apiClient.validateResetToken(
          token,
          controller.signal
        );
        if (cancelled) return;
        setTokenStatus(result.valid ? 'valid' : 'invalid');
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        if (err instanceof PasswordResetError) {
          if (err.status === 429) {
            setCheckRetryAfter(err.retryAfter ?? 60);
            setTokenStatus('rateLimited');
            return;
          }
          if (err.status === 400) {
            setTokenStatus('invalid');
            return;
          }
        }
        setTokenStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, tokenShapeValid]);

  useEffect(() => {
    if (tokenStatus === 'valid' && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
    if (tokenStatus === 'invalid' && invalidHeadingRef.current) {
      invalidHeadingRef.current.focus();
    }
  }, [tokenStatus]);

  const onSubmit = async (data: FormValues) => {
    setPageError(null);
    setLoading(true);
    try {
      await apiClient.resetPassword(token, data.password);
      // Strip the now-consumed token from the URL before navigating away,
      // so a back-button + reload cannot replay the link.
      if (window.history && window.location.search.includes('token=')) {
        window.history.replaceState(
          window.history.state,
          '',
          window.location.pathname
        );
      }
      navigate('/login', { state: { resetSuccess: true }, replace: true });
    } catch (err) {
      if (err instanceof PasswordResetError) {
        if (err.status === 429) {
          setRetryAfter(err.retryAfter ?? 60);
        } else if (err.status === 400 && err.detail) {
          if (err.detail.toLowerCase().includes('token')) {
            setPageError(err.detail);
            setTokenStatus('invalid');
          } else {
            setError('password', { type: 'server', message: err.detail });
          }
        } else {
          setPageError(err.detail || t('auth.reset.errors.default'));
        }
      } else {
        setPageError(t('auth.reset.errors.default'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (tokenStatus === 'invalid') {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <h1
              ref={invalidHeadingRef}
              tabIndex={-1}
              className="text-2xl font-bold focus:outline-none"
            >
              <Translator path="auth.reset.invalidToken.title" />
            </h1>
          </div>
          <Alert variant="error">
            <span aria-live="polite">
              {t('auth.reset.invalidToken.message')}
            </span>
          </Alert>
          <Link
            to="/forgot-password"
            className="text-center text-sm text-primary underline-offset-4 hover:underline"
          >
            <Translator path="auth.reset.actions.requestNewLink" />
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (tokenStatus === 'error') {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-bold">
              <Translator path="auth.reset.title" />
            </h1>
          </div>
          <Alert variant="error">
            <span aria-live="polite">
              {t('auth.reset.errors.checkFailed')}
            </span>
          </Alert>
          <Button
            type="button"
            className="w-full"
            onClick={() => setTokenStatus('checking')}
          >
            <Translator path="auth.reset.actions.retry" />
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const submitDisabled =
    loading ||
    retryAfter !== null ||
    tokenStatus === 'checking' ||
    tokenStatus === 'rateLimited';

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">
            <Translator path="auth.reset.title" />
          </h1>
          <p className="text-sm text-muted-foreground">
            <Translator path="auth.reset.subtitle" />
          </p>
          {tokenStatus === 'checking' && (
            <span
              role="status"
              aria-live="polite"
              aria-busy="true"
              className="text-xs text-muted-foreground"
            >
              {t('auth.reset.checking.label')}
            </span>
          )}
        </div>

        {pageError && (
          <Alert variant="error">
            <span aria-live="polite">{pageError}</span>
          </Alert>
        )}

        {retryAfter !== null && (
          <Alert variant="error">
            <span aria-live="polite">
              {t('auth.reset.errors.rateLimited', { seconds: retryAfter })}
            </span>
          </Alert>
        )}

        {tokenStatus === 'rateLimited' && checkRetryAfter !== null && (
          <Alert variant="error">
            <span aria-live="polite">
              {t('auth.reset.errors.rateLimited', {
                seconds: checkRetryAfter
              })}
            </span>
          </Alert>
        )}

        <div className="grid gap-2">
          <Label htmlFor="password">
            <Translator path="auth.reset.form.password.label" />
          </Label>
          <PasswordInput
            id="password"
            disabled={submitDisabled}
            {...passwordRegister}
            ref={(el) => {
              rhfPasswordRef(el);
              passwordInputRef.current = el;
            }}
            className={errors.password ? 'border-destructive' : ''}
          />
          <p className="text-xs text-muted-foreground">
            <Translator path="auth.reset.form.password.helper" />
          </p>
          {errors.password && (
            <p className="text-sm text-destructive">
              {errors.password.message || t('auth.reset.form.password.invalid')}
            </p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="confirmPassword">
            <Translator path="auth.reset.form.confirmPassword.label" />
          </Label>
          <PasswordInput
            id="confirmPassword"
            disabled={submitDisabled}
            {...register('confirmPassword')}
            className={errors.confirmPassword ? 'border-destructive' : ''}
          />
          {errors.confirmPassword && (
            <p className="text-sm text-destructive">
              {t('auth.reset.form.confirmPassword.mismatch')}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={submitDisabled}>
          <Translator path="auth.reset.actions.submit" />
        </Button>

        <Link
          to="/login"
          className="text-center text-sm text-primary underline-offset-4 hover:underline"
        >
          <Translator path="auth.reset.actions.backToLogin" />
        </Link>
      </form>
    </AuthLayout>
  );
}
